import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// ── Types ─────────────────────────────────────────────────────────────

interface ComponentStatus {
  status: string;
  checked_at: string | null;
  response_time_ms: number;
  detail: string;
  error: string | null;
}

interface AuditItem {
  event_id: string;
  timestamp: string;
  user_id: string;
  event_type: string;
  module: string;
  resource_id?: string;
  resource_type?: string;
  metadata?: string;
  source: string;
}

// ── Constants ─────────────────────────────────────────────────────────

const COMPONENT_LABELS: Record<string, string> = {
  portal_api:         'Portal API',
  mcp_server:         'MCP Server',
  sql_server:         'FileHandler DB',
  dynamodb:           'AWS DynamoDB',
  s3:                 'AWS S3',
  eventbridge_convex: 'Convex Poller',
  eventbridge_bdx:    'BDX Scheduler',
  eventbridge_sla:    'SLA Scheduler',
  lambda_convex:      'Convex Lambda',
};

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  healthy:   { bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500' },
  degraded:  { bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  unhealthy: { bg: 'bg-red-50',    text: 'text-red-700',    dot: 'bg-red-500' },
  unknown:   { bg: 'bg-slate-50',  text: 'text-slate-500',  dot: 'bg-slate-400' },
};

const MODULE_OPTIONS = [
  { value: '', label: 'All modules' },
  { value: 'bdx', label: 'BDX' },
  { value: 'lossrun', label: 'Loss Run' },
  { value: 'convex', label: 'Convex' },
  { value: 'sla', label: 'SLA' },
  { value: 'bluesheet', label: 'Blue Sheet' },
  { value: 'auth', label: 'Auth' },
  { value: 'health', label: 'Health' },
  { value: 'test', label: 'Test' },
];

function timeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Health Tab ────────────────────────────────────────────────────────

function HealthTab() {
  const [components, setComponents] = useState<Record<string, ComponentStatus>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchHealth = useCallback(async () => {
    setError('');
    try {
      const data = await api.getAdminHealth();
      setComponents(data.components || {});
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const healthyCt = Object.values(components).filter(c => c.status === 'healthy').length;
  const total = Object.keys(components).length;
  const overallStatus = healthyCt === total ? 'healthy' : healthyCt >= total - 1 ? 'degraded' : 'unhealthy';
  const overallColors = STATUS_COLORS[overallStatus] || STATUS_COLORS.unknown;

  return (
    <div>
      {/* Overall banner */}
      <div className={`rounded-lg p-4 mb-6 ${overallColors.bg} border`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`w-3 h-3 rounded-full ${overallColors.dot}`} />
            <span className={`font-semibold ${overallColors.text}`}>
              System {overallStatus === 'healthy' ? 'Healthy' : overallStatus === 'degraded' ? 'Degraded' : 'Unhealthy'}
            </span>
            <span className="text-sm text-slate-500">
              {healthyCt}/{total} components healthy
            </span>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-slate-400">
                Updated {timeAgo(lastRefresh.toISOString())}
              </span>
            )}
            <button
              onClick={fetchHealth}
              className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {loading && <p className="text-slate-500 text-sm">Loading health data...</p>}
      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">{error}</p>}

      {/* Component grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(COMPONENT_LABELS).map(([key, label]) => {
          const comp = components[key];
          if (!comp) return null;
          const colors = STATUS_COLORS[comp.status] || STATUS_COLORS.unknown;
          return (
            <div key={key} className={`rounded-lg border p-4 ${colors.bg}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800 text-sm">{label}</h3>
                <span className={`flex items-center gap-1.5 text-xs font-medium ${colors.text}`}>
                  <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  {comp.status}
                </span>
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <div>Last check: {timeAgo(comp.checked_at)}</div>
                <div>Response: {comp.response_time_ms}ms</div>
                <div className="truncate" title={comp.detail}>{comp.detail}</div>
                {comp.error && (
                  <div className="text-red-600 truncate" title={comp.error}>{comp.error}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Activity Log Tab ──────────────────────────────────────────────────

function ActivityLogTab() {
  const [items, setItems] = useState<AuditItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [module, setModule] = useState('');
  const [eventType, setEventType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = { limit: '50' };
      if (module) params.module = module;
      if (eventType) params.event_type = eventType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const data = await api.getAdminAudit(params);
      setItems(data.items || []);
      setHasMore(data.has_more || false);
    } catch (e: any) {
      setError(e.message || 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, [module, eventType, startDate, endDate]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const exportCsv = () => {
    const headers = ['Timestamp', 'Module', 'Event Type', 'Resource', 'User', 'Source', 'Metadata'];
    const rows = items.map(i => [
      i.timestamp,
      i.module,
      i.event_type,
      i.resource_id || '',
      i.user_id,
      i.source,
      `"${(i.metadata || '').replace(/"/g, '""')}"`,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Module</label>
          <select
            value={module}
            onChange={e => setModule(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {MODULE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Event type</label>
          <input
            type="text"
            value={eventType}
            onChange={e => setEventType(e.target.value)}
            placeholder="e.g. report_generated"
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Start date</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">End date</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={exportCsv}
          disabled={items.length === 0}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
        >
          Export CSV
        </button>
      </div>

      {loading && <p className="text-slate-500 text-sm">Loading audit log...</p>}
      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">{error}</p>}

      {!loading && items.length === 0 && (
        <p className="text-slate-500 text-sm">No audit events found.</p>
      )}

      {!loading && items.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-2 px-3 font-semibold text-slate-600">Timestamp</th>
                  <th className="py-2 px-3 font-semibold text-slate-600">Module</th>
                  <th className="py-2 px-3 font-semibold text-slate-600">Event</th>
                  <th className="py-2 px-3 font-semibold text-slate-600">Resource</th>
                  <th className="py-2 px-3 font-semibold text-slate-600">User</th>
                  <th className="py-2 px-3 font-semibold text-slate-600">Source</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.event_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-slate-500 whitespace-nowrap text-xs">
                      {new Date(item.timestamp).toLocaleString()}
                    </td>
                    <td className="py-2 px-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        {item.module}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-700 font-mono text-xs">{item.event_type}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{item.resource_id || '—'}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{item.user_id}</td>
                    <td className="py-2 px-3 text-slate-500 text-xs">{item.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <p className="text-slate-400 text-xs mt-2">Showing first 50 results. Apply filters to narrow.</p>
          )}
        </>
      )}
    </div>
  );
}

// ── Main Admin Dashboard ──────────────────────────────────────────────

export default function AdminDashboard() {
  const [tab, setTab] = useState<'health' | 'activity'>('health');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">System Admin</h1>
        <p className="text-sm text-slate-500 mt-1">Health monitoring and activity log</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button
          onClick={() => setTab('health')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'health'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          System Health
        </button>
        <button
          onClick={() => setTab('activity')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'activity'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Activity Log
        </button>
      </div>

      {tab === 'health' ? <HealthTab /> : <ActivityLogTab />}
    </div>
  );
}
